import type { MediaAsset, RecipeFromImagesRequest, UploadIntentResponse } from '@recipes/contracts';
import { Alert, Button, Textarea } from '@recipes/ui';
import { useMutation } from '@tanstack/react-query';
import { Camera, ImagePlus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  ConstraintsFields,
  EMPTY_CONSTRAINTS,
  toAiConstraints,
  type ConstraintsState,
} from '@/features/ai/constraints-fields';
import { storeGenerationRequest } from '@/features/ai/generation-storage';
import { FormField } from '@/features/shared/form-field';
import { AiConsentNotice } from '@/features/shared/ai-consent-notice';
import { useAiConsent, useConfig, useFeature, useProfile } from '@/features/shared/hooks';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { api } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';

interface PendingImage {
  id: string;
  file: File;
  previewUrl: string;
  progress: number;
  status: 'uploading' | 'ready' | 'error';
  assetId: string | null;
  error?: string;
}

const MAX_IMAGES = 6;
const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

/** Upload with progress events (fetch has no upload progress). */
function uploadWithProgress(
  intent: UploadIntentResponse,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(intent.method, intent.uploadUrl);
    for (const [k, v] of Object.entries(intent.headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed with status ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

export function ImagesGenerationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const aiEnabled = useFeature('ai');
  const imageImport = useFeature('image_import');
  const consented = useAiConsent();
  const { data: config } = useConfig();
  const { data: profile } = useProfile();
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);

  const [images, setImages] = useState<PendingImage[]>([]);
  const [description, setDescription] = useState('');
  const [constraints, setConstraints] = useState<ConstraintsState>(EMPTY_CONSTRAINTS);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (profile && !prefilled) {
      setConstraints((c) => ({ ...c, dietaryPreferences: profile.dietaryPreferences }));
      setPrefilled(true);
    }
  }, [profile, prefilled]);

  useEffect(() => () => images.forEach((i) => URL.revokeObjectURL(i.previewUrl)), [images]);

  const patch = (id: string, changes: Partial<PendingImage>) =>
    setImages((prev) => prev.map((i) => (i.id === id ? { ...i, ...changes } : i)));

  const startUpload = async (item: PendingImage) => {
    try {
      const maxBytes = config?.limits.maxUploadBytes;
      if (maxBytes && item.file.size > maxBytes) throw new Error(t('ai.images.tooLarge'));
      const intent = await api.media.createUploadIntent({
        fileName: item.file.name || 'image.jpg',
        mimeType: item.file.type || 'image/jpeg',
        sizeBytes: item.file.size,
        purpose: 'AI_INPUT',
      });
      await uploadWithProgress(intent, item.file, (pct) => patch(item.id, { progress: pct }));
      const asset: MediaAsset = await api.media.complete(intent.assetId, {});
      patch(item.id, { status: 'ready', progress: 100, assetId: asset.id });
    } catch (error) {
      patch(item.id, { status: 'error', error: describeError(error) });
    }
  };

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const room = MAX_IMAGES - images.length;
    const next: PendingImage[] = Array.from(files)
      .slice(0, Math.max(0, room))
      .map((file) => ({
        id: crypto.randomUUID(),
        file,
        previewUrl: URL.createObjectURL(file),
        progress: 0,
        status: 'uploading',
        assetId: null,
      }));
    setImages((prev) => [...prev, ...next]);
    next.forEach((item) => void startUpload(item));
  };

  const mutation = useMutation({
    mutationFn: (body: RecipeFromImagesRequest) =>
      api.ai.recipeFromImages(body, crypto.randomUUID()),
    onSuccess: (generation, body) => {
      storeGenerationRequest(generation.id, { kind: 'images', body });
      navigate(`/add/ai/${generation.id}`);
    },
  });

  const readyIds = images
    .filter((i) => i.status === 'ready' && i.assetId)
    .map((i) => i.assetId as string);
  const uploading = images.some((i) => i.status === 'uploading');
  const canSubmit = aiEnabled && imageImport && consented && readyIds.length > 0 && !uploading;

  return (
    <PageContainer className="max-w-3xl">
      <FeaturePageHeader
        title={t('ai.images.title')}
        description={t('ai.images.description')}
        backTo="/add"
      />
      {(!aiEnabled || !imageImport) && (
        <Alert variant="warning">{t('errors.FEATURE_DISABLED')}</Alert>
      )}
      {aiEnabled && !consented && <AiConsentNotice />}
      <form
        noValidate
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit)
            mutation.mutate({
              assetIds: readyIds,
              description: description.trim() || undefined,
              constraints: toAiConstraints(constraints, currentLocale()),
            });
        }}
      >
        <Alert variant="info" title={t('ai.images.retentionTitle')}>
          {t('ai.images.retention')}
        </Alert>

        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="sr-only"
            aria-label={t('ai.images.pick')}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            aria-label={t('ai.images.camera')}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="outline"
            disabled={images.length >= MAX_IMAGES}
            onClick={() => inputRef.current?.click()}
          >
            <ImagePlus aria-hidden /> {t('ai.images.pick')}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="md:hidden"
            disabled={images.length >= MAX_IMAGES}
            onClick={() => cameraRef.current?.click()}
          >
            <Camera aria-hidden /> {t('ai.images.camera')}
          </Button>
          <span className="self-center text-xs text-muted-foreground">
            {t('ai.images.max', { count: MAX_IMAGES })}
          </span>
        </div>

        {images.length > 0 && (
          <ul
            className="grid grid-cols-2 gap-3 sm:grid-cols-3"
            aria-label={t('ai.images.selected')}
          >
            {images.map((img) => (
              <li key={img.id} className="relative overflow-hidden rounded-lg border">
                <img src={img.previewUrl} alt="" className="aspect-square w-full object-cover" />
                <div className="space-y-1 p-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{img.file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('ai.images.remove')}
                      onClick={() => setImages((prev) => prev.filter((i) => i.id !== img.id))}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  </div>
                  {img.status === 'uploading' && (
                    <div
                      className="h-1.5 w-full overflow-hidden rounded bg-muted"
                      role="progressbar"
                      aria-valuenow={img.progress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={t('ai.images.uploading')}
                    >
                      <div className="h-full bg-primary" style={{ width: `${img.progress}%` }} />
                    </div>
                  )}
                  {img.status === 'ready' && (
                    <span className="text-success">{t('ai.images.ready')}</span>
                  )}
                  {img.status === 'error' && (
                    <span role="alert" className="text-destructive">
                      {img.error}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <FormField
          label={t('ai.images.descriptionField')}
          hint={t('ai.images.descriptionHint')}
          optionalText={t('common.optional')}
        >
          <Textarea
            rows={3}
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </FormField>
        <ConstraintsFields value={constraints} onChange={setConstraints} showVariants={false} />

        {mutation.error && (
          <Alert variant="destructive" title={t('ai.failed')}>
            {describeError(mutation.error)}
          </Alert>
        )}

        <Button type="submit" size="lg" loading={mutation.isPending} disabled={!canSubmit}>
          {t('ai.images.submit')}
        </Button>
      </form>
    </PageContainer>
  );
}
